import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useZeroXWithdrawalCurrencies } from '../../queries/zero-x';
import { useZeroXWithdrawalFlow } from './useZeroXWithdrawalFlow';
import useLocalization from '../useLocalization';
import useMarketplaceCurrency from '../usePlayerCurrency';
import { useModal } from '../useDialog';

export type { ZeroXWithdrawalPhase, ZeroXWithdrawalMessage } from '../../helpers/payment/zeroXWithdrawal';

const TRANSACTION_HISTORY_PATH = '/my-account/history/transactions';

export const useZeroXWithdrawal = () => {
  const { t } = useLocalization();

  const navigate = useNavigate();
  const { closeModal } = useModal();

  const flow = useZeroXWithdrawalFlow();
  const isDone = flow.phase.name === 'done';

  const schema = useMemo(
    () =>
      z.object({
        amount: z
          .string()
          .trim()
          .min(1, t('zeroXAmountRequired'))
          .regex(/^(0|[1-9]\d{0,11})(\.\d{1,2})?$/, t('zeroXAmountInvalid'))
          .refine((value) => Number(value) > 0, t('amountMustBeGreaterThanZero')),
        currency: z.string().min(1, t('zeroXCurrencyRequired')),
        address: z
          .string()
          .trim()
          .min(1, t('walletAddressRequired'))
          .max(256, t('zeroXAddressTooLong', { max: 256 })),
        tag: z
          .string()
          .trim()
          .max(128, t('zeroXTagTooLong', { max: 128 })),
      }),
    [t]
  );

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: { amount: '', currency: '', address: '', tag: '' },
  });
  const { isSubmitted } = form.formState;

  const amount = useWatch({ control: form.control, name: 'amount' });
  const isAmountValid = schema.shape.amount.safeParse(amount).success;

  const accountCurrency = useMarketplaceCurrency();
  const currencies = useZeroXWithdrawalCurrencies();
  const currency = useWatch({ control: form.control, name: 'currency' });
  const selectedCurrency = currencies.data?.find((option) => option.id === currency);
  const requiresTag = selectedCurrency?.requiresTag ?? false;

  const changeField = (name: keyof z.infer<typeof schema>, value: string) => {
    if (!flow.edit()) {
      return;
    }

    if (name === 'currency' && value !== form.getValues('currency')) {
      form.setValue('address', '');
      form.setValue('tag', '');
      form.clearErrors(['address', 'tag']);
    }

    form.setValue(name, value, { shouldDirty: true, shouldValidate: name === 'amount' || isSubmitted });
  };

  const requestPreview = form.handleSubmit(async (input) => {
    if (!selectedCurrency || currencies.isLoading || currencies.isError) {
      form.setError('currency', { message: t('zeroXWithdrawalUnavailable') });
      return;
    }

    if (requiresTag && !input.tag) {
      form.setError('tag', { message: t('zeroXWithdrawalTagRequired') });
      return;
    }

    await flow.preview({
      amount: input.amount,
      currency: input.currency,
      address: input.address,
      ...(requiresTag ? { tag: input.tag } : {}),
    });
  });

  const reset = () => {
    if (isDone) {
      form.reset();
      flow.reset();
    }
  };

  const viewHistory = () => {
    if (isDone) {
      closeModal();
      navigate(TRANSACTION_HISTORY_PATH);
    }
  };

  return {
    ...flow,

    form,
    isAmountValid,

    currencies,
    selectedCurrency,
    accountCurrency,
    requiresTag,

    changeField,
    requestPreview,
    reset,
    viewHistory,
  };
};
